import { create } from "zustand";
import { suppliersApi } from "@/shared/api/suppliers/api-suppliers";
import { type Person } from "@/shared/hooks/usePeople";
import { type Table } from "@/shared/api/types";
import { getServerMessage } from "@/shared/lib/apiErrorHandler";

const mapSupplierToPerson = (s: {
  supplier_id: string;
  token_id: string;
  name?: string;
  active_token?: boolean;
  tables: Table[];
}): Person => ({
  id: s.supplier_id,
  token_id: s.token_id,
  name: s.name || s.supplier_id,
  status: s.active_token ? "Активен" : "Неактивен",
  balance: "-",
  autoRenewal: false,
  tables: s.tables,
});

interface SuppliersState {
  suppliers: Person[];
  loading: boolean;
  error: string | null;
  selectedSupplierId: string | null;
  fetchSuppliers: () => Promise<void>;
  selectSupplier: (id: string) => void;
}

const SUPPLIERS_LIMIT = 100;

export const useSuppliersStore = create<SuppliersState>((set, get) => ({
  suppliers: [],
  loading: false,
  error: null,
  selectedSupplierId: null,
  fetchSuppliers: async () => {
    set({ loading: true, error: null });
    try {
      const response = await suppliersApi
        .getSuppliersQueryOptions({ limit: SUPPLIERS_LIMIT })
        .queryFn({});
      const mappedSuppliers = response.data.map(mapSupplierToPerson);

      const { selectedSupplierId } = get();
      const isCurrentIdValid = mappedSuppliers.some(
        (s) => s.id === selectedSupplierId,
      );

      set({
        suppliers: mappedSuppliers,
        loading: false,
        selectedSupplierId: isCurrentIdValid
          ? selectedSupplierId
          : (mappedSuppliers[0]?.id ?? null),
      });
    } catch (e: unknown) {
      const errorMessage = getServerMessage(e, "Failed to fetch suppliers");
      set({ error: errorMessage, loading: false });
      console.error(errorMessage);
    }
  },
  selectSupplier: (id: string) => set({ selectedSupplierId: id }),
}));
